import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Loader2, Calendar, MapPin, Users, DollarSign, FileText, 
  CheckCircle, Clock, LogOut, Pen, Download, AlertCircle,
  Phone, Mail, Utensils, Wine, Coffee, ChefHat, Package,
  Table, Sparkles, FileCheck, Printer, ChevronLeft, List
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import { escapeHtml } from '../utils/escapeHtml';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientPortalDashboard = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const signatureRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [allContracts, setAllContracts] = useState([]);
  const [showContractList, setShowContractList] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [signing, setSigning] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const contractPdfRef = useRef(null);
  
  const token = localStorage.getItem('clientPortalToken');
  const clientEmail = localStorage.getItem('clientPortalEmail');

  useEffect(() => {
    if (!token) {
      navigate('/client-portal');
      return;
    }
    fetchContractData();
    fetchAllContracts();
  }, [contractId, token]);

  const fetchAllContracts = async () => {
    try {
      const response = await axios.get(`${API}/api/client-portal/contracts?email=${clientEmail}&token=${token}`);
      setAllContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching all contracts:', error);
    }
  };

  const fetchContractData = async () => {
    try {
      const [contractRes, invoicesRes, paymentRes] = await Promise.all([
        axios.get(`${API}/api/client-portal/contract/${contractId}?token=${token}`),
        axios.get(`${API}/api/client-portal/invoices/${contractId}?token=${token}`),
        axios.get(`${API}/api/payments/status/${contractId}`).catch(() => ({ data: null }))
      ]);
      
      setContract(contractRes.data);
      setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : [invoicesRes.data]);
      if (paymentRes.data) {
        setPaymentStatus(paymentRes.data);
      }
    } catch (error) {
      console.error('Error fetching contract:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('clientPortalToken');
        localStorage.removeItem('clientPortalEmail');
        navigate('/client-portal');
      }
      toast({ title: 'Error loading contract', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clientPortalToken');
    localStorage.removeItem('clientPortalEmail');
    navigate('/client-portal');
  };

  const handleSign = async () => {
    if (!signerName.trim()) {
      toast({ title: 'Please enter your name', variant: 'destructive' });
      return;
    }
    if (signatureRef.current?.isEmpty()) {
      toast({ title: 'Please provide your signature', variant: 'destructive' });
      return;
    }

    setSigning(true);
    try {
      const signatureData = signatureRef.current.toDataURL();
      await axios.post(`${API}/api/client-portal/sign?token=${token}`, {
        contract_id: contractId,
        signer_name: signerName,
        signature_data: signatureData
      });
      
      toast({ title: 'Contract signed successfully!' });
      setShowSignatureModal(false);
      fetchContractData();
    } catch (error) {
      toast({ title: 'Error signing contract', variant: 'destructive' });
    } finally {
      setSigning(false);
    }
  };

  const generateSignedPdf = async () => {
    if (!contract.signed) {
      toast({ title: 'Please sign the contract first', variant: 'destructive' });
      return;
    }

    setGeneratingPdf(true);
    try {
      // Create a hidden div with the contract content for PDF generation
      const pdfContent = document.createElement('div');
      pdfContent.style.padding = '40px';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      pdfContent.style.maxWidth = '800px';
      pdfContent.style.margin = '0 auto';
      pdfContent.style.backgroundColor = 'white';
      
      pdfContent.innerHTML = DOMPurify.sanitize(`
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1e3a5f; padding-bottom: 20px;">
          <h1 style="color: #1e3a5f; font-size: 28px; margin: 0;">Willow Ridge Catering</h1>
          <p style="color: #666; margin: 5px 0;">Catering Contract</p>
        </div>
        
        <div style="background: #f8f8f8; padding: 20px; border-radius: 8px; margin-bottom: 25px;">
          <h2 style="color: #1e3a5f; font-size: 20px; margin: 0 0 10px 0;">${escapeHtml(contract.event_name)}</h2>
          <p style="color: #666; margin: 0;">Contract #: ${escapeHtml(contract.contract_number)}</p>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 25px;">
          <div>
            <h3 style="color: #1e3a5f; font-size: 14px; margin: 0 0 5px 0; text-transform: uppercase;">Client</h3>
            <p style="margin: 0; font-weight: bold;">${escapeHtml(contract.client_name)}</p>
            <p style="margin: 0; color: #666;">${escapeHtml(contract.client_email)}</p>
            ${contract.client_phone ? `<p style="margin: 0; color: #666;">${escapeHtml(contract.client_phone)}</p>` : ''}
          </div>
          <div>
            <h3 style="color: #1e3a5f; font-size: 14px; margin: 0 0 5px 0; text-transform: uppercase;">Event Details</h3>
            <p style="margin: 0;"><strong>Date:</strong> ${escapeHtml(formatDate(contract.event_date))}</p>
            <p style="margin: 0;"><strong>Time:</strong> ${escapeHtml(contract.event_time || contract.event_start_time || 'TBD')}</p>
            <p style="margin: 0;"><strong>Location:</strong> ${escapeHtml(contract.location)}</p>
            <p style="margin: 0;"><strong>Guests:</strong> ${contract.guest_count}</p>
          </div>
        </div>
        
        ${contract.selected_menus_details?.length > 0 ? `
          <div style="margin-bottom: 25px;">
            <h3 style="color: #1e3a5f; font-size: 16px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">Menu Selections</h3>
            ${contract.selected_menus_details.map(menu => `
              <div style="margin: 10px 0; padding: 10px; background: #f8f8f8; border-radius: 4px;">
                <p style="margin: 0; font-weight: bold;">${escapeHtml(menu.name)}</p>
                <p style="margin: 0; color: #666; font-size: 14px;">${menu.category} - $${menu.price_per_person}/person</p>
              </div>
            `).join('')}
          </div>
        ` : ''}
        
        ${contract.selected_hors_doeuvres?.length > 0 ? `
          <div style="margin-bottom: 25px;">
            <h3 style="color: #1e3a5f; font-size: 16px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">Hors d'Oeuvres</h3>
            <p style="margin: 10px 0;">${contract.selected_hors_doeuvres.map(i => i.name || i).join(', ')}</p>
          </div>
        ` : ''}
        
        ${(contract.selected_plated_mains?.length > 0 || contract.selected_plated_salad) ? `
          <div style="margin-bottom: 25px;">
            <h3 style="color: #1e3a5f; font-size: 16px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">Plated Dinner</h3>
            ${contract.selected_plated_salad ? `<p style="margin: 5px 0;"><strong>Salad:</strong> ${contract.selected_plated_salad.name || contract.selected_plated_salad}</p>` : ''}
            ${contract.selected_plated_appetizer ? `<p style="margin: 5px 0;"><strong>Appetizer:</strong> ${contract.selected_plated_appetizer.name || contract.selected_plated_appetizer}</p>` : ''}
            ${contract.selected_plated_mains?.length > 0 ? `<p style="margin: 5px 0;"><strong>Main Courses:</strong> ${contract.selected_plated_mains.map(m => m.name || m).join(', ')}</p>` : ''}
            ${contract.selected_plated_dessert ? `<p style="margin: 5px 0;"><strong>Dessert:</strong> ${contract.selected_plated_dessert.name || contract.selected_plated_dessert}</p>` : ''}
          </div>
        ` : ''}
        
        ${contract.selected_late_night?.length > 0 ? `
          <div style="margin-bottom: 25px;">
            <h3 style="color: #1e3a5f; font-size: 16px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">Late Night Menu</h3>
            <p style="margin: 10px 0;">${contract.selected_late_night.map(i => i.name || i).join(', ')}</p>
          </div>
        ` : ''}
        
        ${(contract.selected_bar?.length > 0 || contract.selected_bar_items?.length > 0) ? `
          <div style="margin-bottom: 25px;">
            <h3 style="color: #1e3a5f; font-size: 16px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">Bar Service</h3>
            <p style="margin: 10px 0;">${[...(contract.selected_bar || []), ...(contract.selected_bar_items || [])].map(i => i.name || i).join(', ')}</p>
          </div>
        ` : ''}
        
        ${contract.special_requests ? `
          <div style="margin-bottom: 25px;">
            <h3 style="color: #1e3a5f; font-size: 16px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">Special Requests</h3>
            <p style="margin: 10px 0; white-space: pre-wrap;">${escapeHtml(contract.special_requests)}</p>
          </div>
        ` : ''}
        
        <div style="margin-bottom: 25px; background: #f8f8f8; padding: 20px; border-radius: 8px;">
          <h3 style="color: #1e3a5f; font-size: 16px; margin: 0 0 15px 0;">Pricing Summary</h3>
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd;">
            <span>Subtotal</span>
            <span>${formatCurrency(contract.subtotal)}</span>
          </div>
          ${contract.addons_subtotal > 0 ? `
            <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd;">
              <span>Add-ons</span>
              <span>${formatCurrency(contract.addons_subtotal)}</span>
            </div>
          ` : ''}
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd;">
            <span>Tax (${((contract.tax_rate || 0.13) * 100).toFixed(0)}%)</span>
            <span>${formatCurrency(contract.tax_amount)}</span>
          </div>
          ${contract.gratuity_amount > 0 ? `
            <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd;">
              <span>Gratuity (${((contract.gratuity_rate || 0) * 100).toFixed(0)}%)</span>
              <span>${formatCurrency(contract.gratuity_amount)}</span>
            </div>
          ` : ''}
          <div style="display: flex; justify-content: space-between; padding: 12px 0; font-weight: bold; font-size: 18px; color: #1e3a5f;">
            <span>Total</span>
            <span>${formatCurrency(contract.total_amount)}</span>
          </div>
          ${contract.deposit_amount > 0 ? `
            <div style="display: flex; justify-content: space-between; padding: 8px 0; color: #666; font-size: 14px;">
              <span>Required Deposit (25%)</span>
              <span>${formatCurrency(contract.deposit_amount)}</span>
            </div>
          ` : ''}
        </div>
        
        ${contract.terms ? `
          <div style="margin-bottom: 25px;">
            <h3 style="color: #1e3a5f; font-size: 16px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">Terms & Conditions</h3>
            <p style="margin: 10px 0; font-size: 12px; white-space: pre-wrap; color: #666;">${contract.terms}</p>
          </div>
        ` : ''}
        
        <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #1e3a5f;">
          <h3 style="color: #1e3a5f; font-size: 16px; margin-bottom: 20px;">Contract Signature</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 40px;">
            <div>
              <p style="margin: 0 0 5px 0; font-weight: bold;">Client Signature:</p>
              ${contract.signature_data ? `<img src="${contract.signature_data}" style="max-width: 250px; max-height: 80px; border-bottom: 1px solid #333;" alt="Signature"/>` : '<div style="height: 80px; border-bottom: 1px solid #333;"></div>'}
              <p style="margin: 10px 0 0 0; font-size: 14px;"><strong>Name:</strong> ${contract.signed_by || '________________'}</p>
              <p style="margin: 5px 0 0 0; font-size: 14px;"><strong>Date:</strong> ${contract.signed_at ? new Date(contract.signed_at).toLocaleDateString() : '________________'}</p>
            </div>
            <div>
              <p style="margin: 0 0 5px 0; font-weight: bold;">Willow Ridge Catering:</p>
              <div style="height: 80px; border-bottom: 1px solid #333;"></div>
              <p style="margin: 10px 0 0 0; font-size: 14px;"><strong>Authorized Representative</strong></p>
              <p style="margin: 5px 0 0 0; font-size: 14px;"><strong>Date:</strong> ________________</p>
            </div>
          </div>
        </div>
        
        <div style="margin-top: 40px; text-align: center; color: #666; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px;">
          <p style="margin: 0;">This is a legally binding contract between ${escapeHtml(contract.client_name)} and Willow Ridge Catering.</p>
          <p style="margin: 5px 0 0 0;">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
      `);
      
      document.body.appendChild(pdfContent);
      
      const options = {
        margin: 10,
        filename: `${contract.contract_number}_signed_contract.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      await html2pdf().set(options).from(pdfContent).save();
      
      document.body.removeChild(pdfContent);
      
      toast({ title: 'PDF downloaded successfully!' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({ title: 'Error generating PDF', variant: 'destructive' });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-lg">Contract not found</p>
          <Button onClick={() => navigate('/client-portal')} className="mt-4">
            Back to Login
          </Button>
        </Card>
      </div>
    );
  }

  const setupItems = [
    { key: 'head_table', label: 'Head Table', icon: Table },
    { key: 'cake_table', label: 'Cake Table', icon: Sparkles },
    { key: 'gift_table', label: 'Gift Table', icon: Package },
    { key: 'dj_table', label: 'DJ Table', icon: Sparkles },
    { key: 'bar_setup', label: 'Bar Setup', icon: Wine },
    { key: 'coffee_station', label: 'Coffee Station', icon: Coffee },
    { key: 'water_station', label: 'Water Station', icon: Utensils },
    { key: 'podium', label: 'Podium', icon: Sparkles },
    { key: 'microphone', label: 'Microphone', icon: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-[#f5f5dc]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="text-3xl text-[#1e3a5f]">🍴</div>
            <div>
              <h1 className="text-xl font-bold text-[#1e3a5f]">Willow Ridge Catering</h1>
              <p className="text-sm text-gray-500">Client Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {allContracts.length > 1 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowContractList(!showContractList)}
                className="border-[#1e3a5f] text-[#1e3a5f]"
              >
                <List size={16} className="mr-2" />
                My Events ({allContracts.length})
              </Button>
            )}
            <span className="text-sm text-gray-600">{clientEmail}</span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Contract List Sidebar */}
      {showContractList && allContracts.length > 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end" onClick={() => setShowContractList(false)}>
          <div 
            className="w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">My Events</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowContractList(false)}
                >
                  ✕
                </Button>
              </div>
              <p className="text-sm opacity-75 mt-1">{allContracts.length} events found</p>
            </div>
            
            <div className="p-4 space-y-3">
              {allContracts.map((c) => (
                <div
                  key={c.id}
                  onClick={() => {
                    navigate(`/client-portal/dashboard/${c.id}`);
                    setShowContractList(false);
                  }}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    c.id === contractId 
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                      : 'border-gray-200 hover:border-[#c9a227] hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-bold text-[#1e3a5f]">{c.event_name}</p>
                      <p className="text-sm text-gray-500">{c.contract_number}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      c.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {c.status?.charAt(0).toUpperCase() + c.status?.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(c.event_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {c.guest_count} guests
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t">
                    <span className="text-sm font-semibold text-[#1e3a5f]">
                      {formatCurrency(c.total_amount)}
                    </span>
                    {c.signed && (
                      <span className="flex items-center gap-1 text-green-600 text-xs">
                        <CheckCircle size={12} />
                        Signed
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Contract Header Card */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-75 mb-1">{contract.contract_number}</p>
                <h2 className="text-3xl font-bold mb-2">{contract.event_name}</h2>
                <p className="text-lg opacity-90">{contract.client_name}</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                contract.status === 'confirmed' ? 'bg-green-500' : 'bg-yellow-500'
              }`}>
                {contract.status?.charAt(0).toUpperCase() + contract.status?.slice(1)}
              </span>
            </div>
          </div>
          
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <Calendar className="text-[#1e3a5f] mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Event Date</p>
                  <p className="font-semibold text-[#1e3a5f]">{formatDate(contract.event_date)}</p>
                  <p className="text-sm text-gray-600">{formatTime(contract.event_time)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="text-[#1e3a5f] mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-semibold text-[#1e3a5f]">{contract.location}</p>
                  <p className="text-sm text-gray-600 capitalize">{contract.location_type}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="text-[#1e3a5f] mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Guest Count</p>
                  <p className="font-semibold text-[#1e3a5f]">{contract.guest_count} guests</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <DollarSign className="text-[#1e3a5f] mt-1" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="font-semibold text-[#1e3a5f] text-xl">{formatCurrency(contract.total_amount)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature Status */}
        <Card className={`mb-6 ${contract.signed ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {contract.signed ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <AlertCircle className="text-amber-600" size={24} />
                )}
                <div>
                  {contract.signed ? (
                    <>
                      <p className="font-semibold text-green-800">Contract Signed</p>
                      <p className="text-sm text-green-600">
                        Signed by {contract.signed_by} on {formatDate(contract.signed_at)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-amber-800">Contract Awaiting Signature</p>
                      <p className="text-sm text-amber-600">Please review and sign this contract</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {contract.signed && (
                  <Button 
                    onClick={generateSignedPdf}
                    disabled={generatingPdf}
                    variant="outline"
                    className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
                    data-testid="download-pdf-btn"
                  >
                    {generatingPdf ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={16} />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download size={16} className="mr-2" />
                        Download PDF
                      </>
                    )}
                  </Button>
                )}
                {!contract.signed && (
                  <Button 
                    onClick={() => setShowSignatureModal(true)}
                    className="bg-[#1e3a5f] hover:bg-[#2d5016]"
                    data-testid="sign-contract-btn"
                  >
                    <Pen size={16} className="mr-2" />
                    Sign Contract
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Status Card */}
        <Card className={`mb-6 ${paymentStatus?.fully_paid ? 'border-green-200 bg-green-50' : paymentStatus?.deposit_paid ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {paymentStatus?.fully_paid ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : paymentStatus?.deposit_paid ? (
                  <CheckCircle className="text-blue-600" size={24} />
                ) : (
                  <DollarSign className="text-amber-600" size={24} />
                )}
                <div>
                  {paymentStatus?.fully_paid ? (
                    <>
                      <p className="font-semibold text-green-800">Payment Complete</p>
                      <p className="text-sm text-green-600">
                        Total paid: {formatCurrency(paymentStatus.total_paid)}
                      </p>
                    </>
                  ) : paymentStatus?.deposit_paid ? (
                    <>
                      <p className="font-semibold text-blue-800">Deposit Received</p>
                      <p className="text-sm text-blue-600">
                        Paid: {formatCurrency(paymentStatus.total_paid)} | Balance due: {formatCurrency(paymentStatus.balance_due)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-amber-800">Payment Pending</p>
                      <p className="text-sm text-amber-600">
                        Deposit required: {formatCurrency(contract.deposit_amount || contract.total_amount * 0.25)} | Total: {formatCurrency(contract.total_amount)}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Contact us to arrange payment</p>
                <p className="text-sm font-medium text-[#1e3a5f]">info@willowridgecatering.com</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Contract Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Timeline */}
            <Card>
              <CardHeader className="bg-[#f5f5dc]">
                <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                  <Clock size={20} />
                  Event Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {contract.cocktail_hour_time && (
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Cocktail Hour</span>
                      <span className="font-semibold">{contract.cocktail_hour_time}</span>
                    </div>
                  )}
                  {contract.dinner_start_time && (
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Dinner Service</span>
                      <span className="font-semibold">{contract.dinner_start_time}</span>
                    </div>
                  )}
                  {contract.late_night_time && (
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Late Night</span>
                      <span className="font-semibold">{contract.late_night_time}</span>
                    </div>
                  )}
                  {contract.event_end_time && (
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Event End</span>
                      <span className="font-semibold">{contract.event_end_time}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Service Style & Setup */}
            <Card>
              <CardHeader className="bg-[#f5f5dc]">
                <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                  <ChefHat size={20} />
                  Service Style & Setup
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Service Style</p>
                    <p className="font-semibold text-[#1e3a5f] capitalize">{contract.service_style || 'N/A'}</p>
                  </div>
                  {contract.plated_courses && (
                    <div>
                      <p className="text-sm text-gray-500">Course Count</p>
                      <p className="font-semibold text-[#1e3a5f]">{contract.plated_courses} Courses</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Table Style</p>
                    <p className="font-semibold text-[#1e3a5f] capitalize">{contract.table_style || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Linens</p>
                    <p className="font-semibold text-[#1e3a5f]">
                      {contract.tablecloth_color} tablecloth, {contract.napkin_color} napkins
                    </p>
                  </div>
                </div>

                <p className="text-sm font-semibold text-gray-700 mb-3">Setup Includes:</p>
                <div className="flex flex-wrap gap-2">
                  {setupItems.filter(item => contract[item.key]).map(item => (
                    <span key={item.key} className="inline-flex items-center gap-1 px-3 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm">
                      <item.icon size={14} />
                      {item.label}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Menu Selections */}
            <Card>
              <CardHeader className="bg-[#f5f5dc]">
                <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                  <Utensils size={20} />
                  Menu Selections
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Main Menus */}
                {contract.selected_menus_details?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3">Selected Menus</h4>
                    {contract.selected_menus_details.map((menu, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg mb-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{menu.name}</p>
                            <p className="text-sm text-gray-500">{menu.category}</p>
                          </div>
                          {menu.price_per_person && (
                            <span className="text-[#1e3a5f] font-semibold">
                              ${menu.price_per_person}/person
                            </span>
                          )}
                        </div>
                        {menu.items?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Includes:</p>
                            <div className="flex flex-wrap gap-1">
                              {menu.items.map((item, i) => (
                                <span key={i} className="text-xs bg-white px-2 py-0.5 rounded border">
                                  {item.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Hors d'Oeuvres */}
                {contract.selected_hors_doeuvres?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3">Hors d'Oeuvres</h4>
                    <div className="flex flex-wrap gap-2">
                      {contract.selected_hors_doeuvres.map((item, idx) => (
                        <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                          {item.name || item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Plated Dinner */}
                {(contract.selected_plated_salad || contract.selected_plated_appetizer || 
                  contract.selected_plated_mains?.length > 0 || contract.selected_plated_dessert) && (
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3">Plated Dinner</h4>
                    <div className="space-y-2">
                      {contract.selected_plated_salad && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-600">Salad</span>
                          <span className="font-medium">{contract.selected_plated_salad.name || contract.selected_plated_salad}</span>
                        </div>
                      )}
                      {contract.selected_plated_appetizer && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-600">Appetizer</span>
                          <span className="font-medium">{contract.selected_plated_appetizer.name || contract.selected_plated_appetizer}</span>
                        </div>
                      )}
                      {contract.selected_plated_mains?.length > 0 && (
                        <div className="py-2 border-b">
                          <span className="text-gray-600">Main Courses</span>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {contract.selected_plated_mains.map((main, idx) => (
                              <span key={idx} className="px-2 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded text-sm">
                                {main.name || main}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {contract.selected_plated_dessert && (
                        <div className="flex justify-between py-2">
                          <span className="text-gray-600">Dessert</span>
                          <span className="font-medium">{contract.selected_plated_dessert.name || contract.selected_plated_dessert}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Late Night */}
                {contract.selected_late_night?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3">Late Night Menu</h4>
                    <div className="flex flex-wrap gap-2">
                      {contract.selected_late_night.map((item, idx) => (
                        <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                          {item.name || item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bar */}
                {(contract.selected_bar?.length > 0 || contract.selected_bar_items?.length > 0) && (
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                      <Wine size={16} />
                      Bar Service
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {contract.selected_bar?.map((item, idx) => (
                        <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                          {item.name || item}
                        </span>
                      ))}
                      {contract.selected_bar_items?.map((item, idx) => (
                        <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                          {item.name || item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rentals */}
                {contract.selected_rentals?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-3">Rentals & Equipment</h4>
                    <div className="space-y-2">
                      {contract.selected_rentals.map((rental, idx) => (
                        <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                          <span>{rental.name || rental}</span>
                          {rental.quantity && <span className="text-gray-500">Qty: {rental.quantity}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Special Requests */}
            {contract.special_requests && (
              <Card>
                <CardHeader className="bg-[#f5f5dc]">
                  <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                    <FileText size={20} />
                    Special Requests
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="whitespace-pre-wrap text-gray-700">{contract.special_requests}</p>
                </CardContent>
              </Card>
            )}

            {/* Terms & Conditions */}
            {contract.terms && (
              <Card>
                <CardHeader className="bg-[#f5f5dc]">
                  <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                    <FileCheck size={20} />
                    Terms & Conditions
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="whitespace-pre-wrap text-gray-700 text-sm">{contract.terms}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Financial Summary */}
          <div className="space-y-6">
            {/* Pricing Breakdown */}
            <Card>
              <CardHeader className="bg-[#f5f5dc]">
                <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                  <DollarSign size={20} />
                  Pricing Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{formatCurrency(contract.subtotal)}</span>
                  </div>
                  {contract.addons_subtotal > 0 && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Add-ons</span>
                      <span className="font-medium">{formatCurrency(contract.addons_subtotal)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Tax ({((contract.tax_rate || 0.13) * 100).toFixed(0)}%)</span>
                    <span className="font-medium">{formatCurrency(contract.tax_amount)}</span>
                  </div>
                  {contract.gratuity_amount > 0 && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Gratuity ({((contract.gratuity_rate || 0) * 100).toFixed(0)}%)</span>
                      <span className="font-medium">{formatCurrency(contract.gratuity_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 text-lg font-bold">
                    <span className="text-[#1e3a5f]">Total</span>
                    <span className="text-[#1e3a5f]">{formatCurrency(contract.total_amount)}</span>
                  </div>
                  {contract.deposit_amount > 0 && (
                    <div className="flex justify-between py-2 border-t text-sm">
                      <span className="text-gray-500">Required Deposit (25%)</span>
                      <span className="text-gray-600">{formatCurrency(contract.deposit_amount)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment Status */}
            <Card>
              <CardHeader className="bg-[#f5f5dc]">
                <CardTitle className="text-[#1e3a5f]">Payment Status</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {invoices.length > 0 ? (
                  <div className="space-y-4">
                    {invoices.map((invoice, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{invoice.invoice_number}</p>
                            <p className="text-xs text-gray-500">
                              Due: {formatDate(invoice.due_date)}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            invoice.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-700'
                              : invoice.payment_status === 'partial'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {invoice.payment_status?.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Total</span>
                            <span>{formatCurrency(invoice.total_amount)}</span>
                          </div>
                          {invoice.deposit_paid && (
                            <div className="flex justify-between text-green-600">
                              <span>Deposit Paid</span>
                              <span>-{formatCurrency(invoice.deposit_amount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-semibold pt-2 border-t">
                            <span>Balance Due</span>
                            <span className={invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'}>
                              {formatCurrency(invoice.balance_due)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No invoices yet</p>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader className="bg-[#f5f5dc]">
                <CardTitle className="text-[#1e3a5f]">Questions?</CardTitle>
              </CardHeader>
              <CardContent className="p-6 text-center">
                <p className="text-gray-600 mb-4">Contact Willow Ridge Catering</p>
                <div className="space-y-2 text-sm">
                  <a href="tel:+15551234567" className="flex items-center justify-center gap-2 text-[#1e3a5f] hover:underline">
                    <Phone size={16} />
                    (555) 123-4567
                  </a>
                  <a href="mailto:info@willowridgecatering.com" className="flex items-center justify-center gap-2 text-[#1e3a5f] hover:underline">
                    <Mail size={16} />
                    info@willowridgecatering.com
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Sign Contract</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                By signing below, you agree to the terms and conditions of this contract.
              </p>
              
              <div>
                <Label htmlFor="signerName">Your Full Name</Label>
                <Input
                  id="signerName"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <Label>Signature</Label>
                <div className="border rounded-lg bg-white">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-40',
                      style: { width: '100%', height: '160px' }
                    }}
                  />
                </div>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => signatureRef.current?.clear()}
                  className="text-xs"
                >
                  Clear Signature
                </Button>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowSignatureModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSign}
                  disabled={signing}
                  className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5016]"
                >
                  {signing ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Pen size={16} className="mr-2" />
                      Sign Contract
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default ClientPortalDashboard;
